<template>
  <main-template>
    <v-row>
      <v-col>
        <h1>
          <v-btn
            color="primary"
            x-small
            text
            @click="() => $router.push({ name: 'turmas' })"
            :disabled="false"
          >
            <v-icon left> fa fa-angle-left </v-icon>
          </v-btn>
          Informações sobre Frequência da Turma - Bolsa Família
        </h1>
      </v-col>
    </v-row>
    <v-card class="mx-auto my-3" elevation="2">
      <v-card-title class="primary--text">
        <v-icon left color="primary">mdi-account-group</v-icon>
        Turma
        <v-spacer></v-spacer>
        <v-chip v-if="turma" small color="primary lighten-5" text-color="primary">
          {{ turma.turno?.descricao || "- - -" }}
        </v-chip>
      </v-card-title>

      <v-divider class="mx-4"></v-divider>

      <v-card-text class="pt-4">
        <!-- Descrição Principal -->
        <div class="mb-4">
          <div class="text-h6 font-weight-medium secondary--text">
            {{ turma?.descricao || "Turma não identificada" }}
          </div>
          <div v-if="turma?.curso" class="text-caption grey--text">
            Código: {{ turma.codigo || "------" }}
          </div>
        </div>

        <!-- Detalhes do Curso -->
        <v-list dense class="transparent">
          <v-list-item>
            <v-list-item-icon>
              <v-icon small>mdi-book-open-variant</v-icon>
            </v-list-item-icon>
            <v-list-item-content>
              <v-list-item-title class="font-weight-medium">
                {{ turma?.curso?.descricao || "Curso não informado" }}
              </v-list-item-title>
              <v-list-item-subtitle v-if="turma?.curso">
                Carga horária: {{ carga_horaria_total ?? "- - -" }}
                <span v-if="carga_horaria_total">h</span>
              </v-list-item-subtitle>
            </v-list-item-content>
          </v-list-item>

          <v-divider inset class="my-2"></v-divider>

          <v-list-item>
            <v-list-item-icon>
              <v-icon small>mdi-chart-line</v-icon>
            </v-list-item-icon>
            <v-list-item-content>
              <v-list-item-title>
                <span class="font-weight-regular">Frequência mínima:</span>
                <span class="primary--text font-weight-bold">
                  {{
                    turma?.curso?.frequencia_minima
                      ? `${turma.curso.frequencia_minima.toFixed(2)}%`
                      : "Não definida"
                  }}
                </span>
              </v-list-item-title>
            </v-list-item-content>
          </v-list-item>
        </v-list>
      </v-card-text>

      <v-card-actions v-if="turma" class="px-4">
        <v-chip small color="green lighten-5" text-color="green darken-2">
          <v-icon left small>mdi-calendar</v-icon>
          {{ meses[0] }} / {{ meses[1] }}
        </v-chip>
        <v-divider vertical class="ms-2"></v-divider>
        <v-btn @click="gerarRelatorio()" small text color="primary">
          Exportar
          <v-icon right small>mdi-chevron-right</v-icon>
        </v-btn>
      </v-card-actions>
    </v-card>

    <v-row class="mt-2">
      <v-col cols="12" class="text-right">
        <v-btn-toggle tile color="deep-orange accent-3" group mandatory>
          <v-btn
            v-for="(grupo, index) in grupo_meses"
            :key="index"
            @click="() => handleGrupo(index)"
          >
            {{ grupo.map((g) => g.mes).join(" e ") }}
          </v-btn>
        </v-btn-toggle>
      </v-col>
      <v-col cols="12">
        <v-card class="mt-2">
          <v-card>
            <v-card-title class="primary white--text">
              <v-icon left color="white">mdi-account-group</v-icon>
              Frequência de Alunos
              <v-spacer></v-spacer>
              <v-text-field
                v-model="search"
                append-icon="mdi-magnify"
                label="Pesquisar aluno..."
                single-line
                hide-details
                class="shrink"
                color="white"
              ></v-text-field>
            </v-card-title>

            <v-data-table
              :headers="headers"
              :items-per-page="alunos.length"
              :items="alunos"
              :search="search"
              hide-default-footer
              class="elevation-1"
              dense
              must-sort
              :header-props="{ sortIcon: 'mdi-menu-down' }"
            >
              <template v-slot:item.cod="{ item }">
                {{ alunos.indexOf(item) + 1 }}
              </template>

              <template v-slot:item.percentual_frequencia_mes1="{ item }">
                <v-chip
                  small
                  :color="
                    item.percentual_frequencia_mes1 < turma.curso.frequencia_minima
                      ? 'error lighten-5'
                      : 'green'
                  "
                  :text-color="
                    item.percentual_frequencia_mes1 < turma.curso.frequencia_minima
                      ? 'error'
                      : 'white'
                  "
                >
                  {{ item.percentual_frequencia_mes1 }}%
                  <v-icon
                    right
                    small
                    v-if="item.percentual_frequencia_mes1 < turma.curso.frequencia_minima"
                  >
                    mdi-alert
                  </v-icon>
                </v-chip>
              </template>

              <template v-slot:item.motivo_faltas_mes1="{ item }">
                <v-select
                  dense
                  :items="$constants.motivos_faltas"
                  v-model="valores_motivos[item.matricula_id + '_' + item.mes_1]"
                  item-value="codigo"
                  :item-text="(i) => `${i.codigo} - ${i.descricao?.substr(0, 60).concat('...')}`"
                  @change="(event) => saveData(event, item, item.mes_1)"
                  :disabled="item.percentual_frequencia_mes1 >= turma?.curso?.frequencia_minima"
                ></v-select>
              </template>
              <template v-slot:item.percentual_frequencia_mes2="{ item }">
                <v-chip
                  small
                  :color="
                    item.percentual_frequencia_mes2 < turma.curso.frequencia_minima
                      ? 'error lighten-5'
                      : 'green'
                  "
                  :text-color="
                    item.percentual_frequencia_mes2 < turma.curso.frequencia_minima
                      ? 'error'
                      : 'white'
                  "
                >
                  {{ item.percentual_frequencia_mes2 }}%
                  <v-icon
                    right
                    small
                    v-if="item.percentual_frequencia_mes2 < turma.curso.frequencia_minima"
                  >
                    mdi-alert
                  </v-icon>
                </v-chip>
              </template>
              <template v-slot:item.motivo_faltas_mes2="{ item }">
                <v-select
                  dense
                  :items="$constants.motivos_faltas"
                  v-model="valores_motivos[item.matricula_id + '_' + item.mes_2]"
                  item-value="codigo"
                  :item-text="(i) => `${i.codigo} - ${i.descricao?.substr(0, 60).concat('...')}`"
                  @change="(event) => saveData(event, item, item.mes_2)"
                  :disabled="item.percentual_frequencia_mes2 >= turma?.curso?.frequencia_minima"
                ></v-select>
              </template>
            </v-data-table>
          </v-card>
        </v-card>
      </v-col>
    </v-row>
  </main-template>
</template>

<script>
export default {
  data() {
    return {
      turma: null,
      turmaId: null,
      alunos: [],
      motivos_faltas_list: [],
      valores_motivos: {},
      search: "",
      headers: [
        { text: "#", value: "cod" },
        { text: "Nome do Aluno", value: "nome", align: "center", sortable: true },
        { text: "Matrícula", value: "matricula", align: "center", sortable: false },
        { text: "Frequência Mês 1 (%)", value: "percentual_frequencia_mes1", align: "center" },
        { text: "Frequência Mês 2 (%)", value: "percentual_frequencia_mes2", align: "center" },
      ],
      grupo_atual: 1,
      meses: [],
      grupo_meses: {
        1: [
          { id: 2, mes: "Fevereiro" },
          { id: 3, mes: "Março" },
        ],
        2: [
          { id: 4, mes: "Abril" },
          { id: 5, mes: "Maio" },
        ],
        3: [
          { id: 6, mes: "Junho" },
          { id: 7, mes: "Julho" },
        ],
        4: [
          { id: 8, mes: "Agosto" },
          { id: 9, mes: "Setembro" },
        ],
        5: [
          { id: 10, mes: "Outubro" },
          { id: 11, mes: "Novembro" },
        ],
      },
    };
  },
  async mounted() {
    this.getTurmaId();
    this.grupo_atual = 1;
    await this.loadData();
  },
  methods: {
    getTurmaId() {
      const { id } = this.$route.params;
      this.turmaId = id;
    },
    getColor(percentual) {
      if (percentual < 60) return "red";
      if (percentual < 75) return "orange";
      return "green";
    },
    async handleGrupo(index) {
      if (index === this.grupo_atual) return;
      this.grupo_atual = index;
      await this.loadData();
    },

    getMotivoFaltaDaMatricula(matricula_id, mes) {
      return this.motivos_faltas_list
        .find((m) => m.id === matricula_id)
        ?.motivos_faltas.find((motivo) => motivo.mes === mes)?.codigo;
    },

    async loadData() {
      try {
        this.$loaderService.open("Carregando dados");

        const [descricao_mes_1, descricao_mes_2] = this.getMeses();

        this.updateHeaders(descricao_mes_1, descricao_mes_2);

        const [cod_mes_1, cod_mes_2] = this.getMeses("id");
        await this.getAlunos(cod_mes_1, cod_mes_2);
        await this.pegarMotivosFaltas(cod_mes_1, cod_mes_2);
        this.updateContent(cod_mes_1, cod_mes_2);

        this.$loaderService.close();
      } catch (error) {
        this.$handleError(error);
      }
    },

    updateHeaders(descricao_mes_1, descricao_mes_2) {
      this.meses = [descricao_mes_1, descricao_mes_2];
      this.headers = [
        { text: "#", value: "cod" },
        { text: "Nome do Aluno", value: "aluno", sortable: true },
        {
          text: `Frequencia ${descricao_mes_1} (%)`,
          value: "percentual_frequencia_mes1",
          align: "center",
        },
        {
          text: `Motivo Faltas ${descricao_mes_1}`,
          value: "motivo_faltas_mes1",
          align: "center",
        },
        {
          text: `Frequencia ${descricao_mes_2} (%)`,
          value: "percentual_frequencia_mes2",
          align: "center",
        },
        {
          text: `Motivo Faltas ${descricao_mes_2}`,
          value: "motivo_faltas_mes2",
          align: "center",
        },
      ];
    },

    getMeses(tipo = null) {
      return this.grupo_meses[Number.parseInt(this.grupo_atual, 10)].map((m) =>
        tipo === "id" ? m.id : m.mes
      );
    },

    async gerarRelatorio() {
      try {
        this.$loaderService.open("Gerando relatório");
        const [mes_1, mes_2] = this.getMeses("id");

        await this.$services.turmasService.gerarRelatorioFrequenciaBolsaFamilia(
          this.turmaId,
          mes_1,
          mes_2
        );

        this.$toast.success("Relatório gerado com sucesso!");
        this.$loaderService.close();
      } catch (error) {
        this.$handleError(error);
      }
    },

    updateContent(cod_mes_1, cod_mes_2) {
      this.motivos_faltas_list.forEach((matricula) => {
        const key1 = `${matricula.id}_${cod_mes_1}`;
        const key2 = `${matricula.id}_${cod_mes_2}`;

        this.$set(
          this.valores_motivos,
          key1,
          this.getMotivoFaltaDaMatricula(matricula.id, cod_mes_1)
        );
        this.$set(
          this.valores_motivos,
          key2,
          this.getMotivoFaltaDaMatricula(matricula.id, cod_mes_2)
        );
      });
    },

    async saveData(event, item, mes) {
      try {
        this.$loaderService.open("Salvando dados");
        await this.$services.turmasService.salvarMotivosFaltas({
          codigo: event,
          turma_id: this.turmaId,
          matricula_id: item.matricula_id,
          mes,
        });
        this.$toast.success("Dados salvos com sucesso!");
        this.$loaderService.close();
      } catch (error) {
        this.$handleError(error);
      }
    },

    async pegarMotivosFaltas(cod_mes_1, cod_mes_2) {
      try {
        this.$loaderService.open("Carregando alunos");

        const data = await this.$services.turmasService.pegarMotivosFaltas(
          this.turmaId,
          cod_mes_1,
          cod_mes_2
        );

        this.motivos_faltas_list = data.motivos;

        this.$loaderService.close();
      } catch (error) {
        this.$handleError(error);
      }
    },

    async getAlunos(mes1, mes2) {
      try {
        const data = await this.$services.turmasService.bolsaFamilia(this.turmaId, mes1, mes2);

        this.turma = data.turma;
        this.carga_horaria_total = data.carga_horaria_total;
        this.alunos = data.alunos;
      } catch (error) {
        this.$handleError(error);
      }
    },
  },
};
</script>

<style></style>
